import type { AuthSession } from '~/types/domain'
import { sessionToActor } from '~/stores/useAuthStore'

type SessionState = 'unknown' | 'authenticated' | 'refreshing' | 'guest'

/**
 * Three-value outcome from refresh():
 * - 'refreshed': we just rotated. Cookies are fresh. Caller should retry.
 * - 'skipped':   guard fired (a refresh happened in the last 30 s). Cookies
 *                are already fresh. Caller should also retry.
 * - 'failed':    the refresh genuinely 401'd. We've set state='guest' and
 *                broadcast logout. Caller should fall through to redirect.
 */
type RefreshOutcome = 'refreshed' | 'skipped' | 'failed'

interface SessionApi {
  state: { readonly value: SessionState }
  ready: Promise<void>
  /** `force` skips the guest fast-path — used when fresh cookies are known to
   *  exist despite an empty persisted actor (e.g. a sibling tab logged in). */
  bootstrap: (opts?: { force?: boolean }) => Promise<void>
  refresh: () => Promise<RefreshOutcome>
  login: (email: string, password: string, remember: boolean) => Promise<void | { challenge: string, session: string, username: string }>
  verifyEmail: (email: string, code: string) => Promise<void>
  /** Adopt a session the server issued outside login (e.g. claiming a user
   *  from an invitation link, which sets the cookies and returns the actor). */
  adopt: (s: AuthSession) => void
  logout: () => Promise<void>
  /** Platform admin starts acting as `userId`. Throws if the server refuses. */
  startAssume: (userId: string, reason: string) => Promise<void>
  /** End the assume session and return to the admin's own session. */
  stopAssume: () => Promise<void>
  /** Called by the cross-tab plugin when a sibling tab broadcasts 'refreshed'. */
  noteSiblingRefresh: (at: number, expiresInSecs: number) => void
}

let singleton: SessionApi | null = null

export function useSession(): SessionApi {
  if (singleton) return singleton

  const state = ref<SessionState>('unknown')
  const authStore = useAuthStore()
  const auth = useAuth()

  let inflightRefresh: Promise<RefreshOutcome> | null = null
  let inflightStop: Promise<void> | null = null
  let lastRefreshedAt = 0
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let resolveReady: () => void
  const ready = new Promise<void>((r) => { resolveReady = r })

  function broadcast(
    msg:
      | { type: 'login' | 'logout', at?: number }
      | { type: 'refreshed', at: number, expiresInSecs: number }
  ) {
    if (import.meta.client) {
      try {
        const ch = (window as unknown as { __authChannel?: BroadcastChannel }).__authChannel
        ch?.postMessage(msg)
      } catch {}
      try {
        localStorage.setItem(`auth:tick:${msg.type}`, String(msg.at ?? Date.now()))
      } catch {}
    }
  }

  function scheduleIdleRefresh(accessExpiresInSecs: number) {
    if (!import.meta.client) return
    if (idleTimer) clearTimeout(idleTimer)
    const headroomMs = accessExpiresInSecs * 1000 - 60_000
    const jitterMs = Math.random() * 10_000 - 5_000
    const delay = Math.max(15_000, headroomMs + jitterMs)
    idleTimer = setTimeout(() => { void refresh() }, delay)
  }

  function cancelIdleRefresh() {
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null }
  }

  function applySession(s: AuthSession) {
    authStore.setActor(sessionToActor(s))
    authStore.setAssumedBy(s.assumed_by ?? null)
    state.value = 'authenticated'
    // An assume session has no refresh token; it ends at its fixed expiry.
    if (s.assumed_by) cancelIdleRefresh()
    else scheduleIdleRefresh(s.access_expires_in_secs)
  }

  function fail() {
    authStore.reset()
    state.value = 'guest'
    cancelIdleRefresh()
  }

  async function bootstrap(opts?: { force?: boolean }) {
    // Guest fast-path: the persisted actor is our session marker. A browser
    // with no trace of a prior login resolves to guest with zero network
    // calls — /me is probed only when there was a session to recover.
    // (frontend ADR-0001)
    if (!opts?.force && !authStore.isLoggedIn) {
      fail()
      resolveReady()
      return
    }
    try {
      const s = await auth.fetchMe()
      if (s) {
        applySession(s)
      } else if (state.value !== 'authenticated' && state.value !== 'guest') {
        // An expired assume session makes /me trigger stopAssume, which already
        // restored the admin (or ended as guest) before fetchMe returned null.
        const refreshed = await refresh()
        if (refreshed !== 'refreshed' && refreshed !== 'skipped') fail()
      }
    } catch {
      fail()
    } finally {
      resolveReady()
    }
  }

  async function refresh(): Promise<RefreshOutcome> {
    if (inflightRefresh) return inflightRefresh
    if (Date.now() - lastRefreshedAt < 30_000) return 'skipped'

    state.value = 'refreshing'
    inflightRefresh = (async () => {
      try {
        const s = await auth.refreshSession()
        if (s) {
          lastRefreshedAt = Date.now()
          applySession(s)
          broadcast({ type: 'refreshed', at: lastRefreshedAt, expiresInSecs: s.access_expires_in_secs })
          return 'refreshed' as const
        }
        fail()
        broadcast({ type: 'logout' })
        return 'failed' as const
      } finally {
        inflightRefresh = null
      }
    })()
    return inflightRefresh
  }

  function noteSiblingRefresh(at: number, expiresInSecs: number) {
    // A sibling tab just refreshed. The HttpOnly cookies are now fresh
    // for us too (same-origin cookies are shared). Update the guard so
    // our own refresh() correctly returns 'skipped' if it fires next,
    // and reschedule our idle timer using the broadcast TTL so we
    // don't race the sibling's next refresh.
    lastRefreshedAt = at
    scheduleIdleRefresh(expiresInSecs)
  }

  async function login(email: string, password: string, remember: boolean) {
    const result = await auth.login(email, password, remember)
    if (result && typeof result === 'object' && 'challenge' in result) {
      return result as { challenge: string, session: string, username: string }
    }
    // /auth/login now returns the full actor envelope; no follow-up /me.
    applySession(result as AuthSession)
    broadcast({ type: 'login' })
  }

  async function verifyEmail(email: string, code: string) {
    const s = await auth.confirmSignup(email, code)
    applySession(s)
    broadcast({ type: 'login' })
  }

  function adopt(s: AuthSession) {
    applySession(s)
    broadcast({ type: 'login' })
  }

  async function logout() {
    // The server's logout only clears assume_token, so signing out of an
    // assumed session returns the admin to their own session instead.
    if (authStore.isAssumed) return stopAssume()
    if (inflightRefresh) {
      try { await inflightRefresh } catch {}
    }
    try { await auth.logout() } catch {}
    fail()
    broadcast({ type: 'logout' })
    const router = useRouter()
    if (!router.currentRoute.value.meta.public) {
      await navigateTo('/login')
    }
  }

  async function startAssume(userId: string, reason: string) {
    const s = await auth.startAssume(userId, reason)
    applySession(s)
    // Cookies are shared: sibling tabs re-read /me and become the user too.
    broadcast({ type: 'login' })
    // Full page load, not a client-side route change: stores still hold the
    // admin's data (e.g. every organizer) and must not carry into the user's
    // session.
    await navigateTo('/app/dashboard', { external: true })
  }

  function stopAssume(): Promise<void> {
    // Several requests can hit an expired assume session at once; they must
    // share one stop instead of each restoring the admin session.
    if (inflightStop) return inflightStop
    inflightStop = (async () => {
      try {
        // If this throws the cookie is still set, so do not probe /me: it
        // would answer assume_expired and re-enter this function.
        await auth.stopAssume()
        authStore.setAssumedBy(null)
        const s = await auth.fetchMe()
        if (s) {
          applySession(s)
          broadcast({ type: 'login' })
          // Not awaited: the auth middleware waits on `ready`, which bootstrap
          // may only resolve after this stop (triggered from its /me) returns.
          // Full page load so the user's cached data is dropped too.
          void navigateTo('/app/admin/users', { external: true })
        } else {
          fail()
          broadcast({ type: 'logout' })
          void navigateTo('/login')
        }
      } finally {
        inflightStop = null
      }
    })()
    return inflightStop
  }

  singleton = { state, ready, bootstrap, refresh, login, verifyEmail, adopt, logout, startAssume, stopAssume, noteSiblingRefresh }
  return singleton
}

/** Test-only: drop the singleton between specs. */
export function __resetUseSessionForTests() {
  singleton = null
}
